import React, { useEffect, useRef } from 'react';
import useMaterialEstimatingCalculator from './hooks/useMaterialEstimatingCalculator';
import Input from '../../components/Input';
import Button from '../../components/Button';
import SelectTopLabel from '../../components/SelectTopLabel';
import Checkbox from '../../components/Checkbox';
import Modal from '../modal';
import {
  cleanAndDownloadPdf,
  formatNumber,
  handleTooltipShow
} from '../../helpers/calculator';

function MaterialEstimatingCalculator(props) {
  const savePdfButtonRef = useRef(null);
  const printContainerRef = useRef(null);
  const resultsRef = useRef(null);

  const { labels, productNames, productCoverageLabels, ...config } =
    useMaterialEstimatingCalculator({
      ...props
    });

  const { downloadEndpoint, productTooltips } = props;
  const tooltips = JSON.parse(productTooltips);

  const calculatorDisclaimerDiv = (
    <div
      className="material-estimating-calculator__disclaimer"
      dangerouslySetInnerHTML={{ __html: props.calculatorDisclaimerRtf }}
    />
  );

  const saveDisclaimerDiv = (
    <div
      className="material-estimating-calculator__disclaimer material-estimating-calculator__disclaimer--save"
      dangerouslySetInnerHTML={{ __html: props.saveDisclaimerRtf }}
    />
  );

  useEffect(() => {
    if (savePdfButtonRef.current) {
      const modal = document.querySelector(`#${labels.calculatorModalId}`);
      new Modal(modal);
    }

    if (resultsRef.current && config.displayResults) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [config.displayResults]);

  const handleClickSaveAsPdf = () => {
    cleanAndDownloadPdf(
      printContainerRef,
      labels.downloadDelayTime,
      labels.downloadFileName,
      downloadEndpoint
    );
  };

  const renderMobileSections = (
    heading,
    dropdownValue,
    setDropdownValue,
    options
  ) => {
    const _options = typeof options === 'function' ? options() : options;
    const managedCoverageLabel = productCoverageLabels[dropdownValue];

    return (
      <>
        {heading && (
          <p className="material-estimating-calculator__results-table-title">
            {heading}
          </p>
        )}
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label material-estimating-calculator__results-label--blue">
            {labels.materialHeading}
          </p>
          <select
            className="material-estimating-calculator__results-select"
            onChange={e => setDropdownValue(e.target.value)}
            value={dropdownValue}
          >
            {_options?.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.typicalCoverageHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {managedCoverageLabel ||
              config.results[dropdownValue].typicalCoverage}
          </p>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.unitSizeHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {config.results[dropdownValue].unitSize}
          </p>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.unitQuantityHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {config.results[dropdownValue].unitQuantityRoundedUp}
          </p>
        </div>
      </>
    );
  };

  const renderDesktopResultsRow = product => {
    const tooltip = tooltips[product];
    const managedCoverageLabel = productCoverageLabels[product];

    return (
      <tr>
        <td>
          <span
            className={
              tooltip ? 'material-estimating-calculator__tooltip-wrapper' : null
            }
          >
            {productNames[product]}
            {tooltip && (
              <button
                className="material-estimating-calculator__tooltip"
                aria-label="tooltip"
                onMouseOver={handleTooltipShow}
                onFocus={handleTooltipShow}
                onTouchStart={handleTooltipShow}
              >
                <div
                  className="material-estimating-calculator__tooltip-box"
                  dangerouslySetInnerHTML={{ __html: tooltip }}
                ></div>
              </button>
            )}
          </span>
        </td>
        <td>
          {managedCoverageLabel || config.results[product].typicalCoverage}
        </td>
        <td>{config.results[product].unitSize}</td>
        <td>{config.results[product].unitQuantityRoundedUp}</td>
      </tr>
    );
  };

  const resultsRender =
    //config.resinTypes: '', terapro220, terapro250, terapro250grid // this is needed to filter the results
    config.displayResults && (
      <>
        <div
          className="material-estimating-calculator__results"
          ref={resultsRef}
        >
          <h2 className="material-estimating-calculator__results-heading">
            {labels.estimatedResultsHeading}
          </h2>
          {labels.estimatedResultsCopy && (
            <p className="material-estimating-calculator__results-copy">
              {labels.estimatedResultsCopy}
            </p>
          )}
          <h3 className="material-estimating-calculator__results-subheading">
            {labels.flashingSystemHeading}
          </h3>
          {/* This is for all the resin types */}
          <div className="material-estimating-calculator__results-table-wrapper">
            <div className="material-estimating-calculator__results-grid">
              {renderMobileSections(
                labels.primerLayerHeading,
                config.flashingPrimerLayerDropdownValue,
                config.setFlashingPrimerLayerDropdownValue,
                [
                  { value: 'proPrimerW', label: productNames.proPrimerW },
                  {
                    value: 'proPrimerCatalystLiquid',
                    label: productNames.proPrimerCatalystLiquid
                  }
                ]
              )}
              {renderMobileSections(
                labels.flashingLayerHeading,
                config.flashingLayerDropdownValue,
                config.setFlashingLayerDropdownValue,
                [
                  {
                    value: 'pmmaFlashing',
                    label: productNames.pmmaFlashing
                  },
                  {
                    value: 'pmmaFlashingCatalystLiquid',
                    label: productNames.pmmaFlashingCatalystLiquid
                  },
                  {
                    value: 'proFleeceReinforcement',
                    label: productNames.proFleeceReinforcement
                  }
                ]
              )}
              {config.includeProColorFinish &&
                renderMobileSections(
                  labels.colorFinishLayerHeading,
                  config.colorFinishLayerDropdownValue,
                  config.setColorFinishLayerDropdownValue,
                  [
                    {
                      value: 'proColorFinish',
                      label: productNames.proColorFinish
                    },
                    {
                      value: 'proColorFinishCatalystLiquid',
                      label: productNames.proColorFinishCatalystLiquid
                    }
                  ]
                )}
            </div>
            <table className="material-estimating-calculator__results-table">
              <thead>
                <tr>
                  <th className="material-estimating-calculator__results-table-title">
                    {labels.primerLayerHeading}
                  </th>
                </tr>
                <tr>
                  <th>{labels.materialHeading}</th>
                  <th>{labels.typicalCoverageHeading}</th>
                  <th>{labels.unitSizeHeading}</th>
                  <th>{labels.unitQuantityHeading}</th>
                </tr>
              </thead>
              <tbody>
                {renderDesktopResultsRow('proPrimerW')}
                {renderDesktopResultsRow('proPrimerCatalystLiquid')}
                <tr>
                  <td className="material-estimating-calculator__results-table-title">
                    {labels.flashingLayerHeading}
                  </td>
                </tr>
                {renderDesktopResultsRow('pmmaFlashing')}
                {renderDesktopResultsRow('pmmaFlashingCatalystLiquid')}
                {renderDesktopResultsRow('proFleeceReinforcement')}
                {config.includeProColorFinish && (
                  <>
                    <tr>
                      <td className="material-estimating-calculator__results-table-title">
                        {labels.colorFinishLayerHeading}
                      </td>
                    </tr>
                    {renderDesktopResultsRow('proColorFinish')}
                    {renderDesktopResultsRow('proColorFinishCatalystLiquid')}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <h3 className="material-estimating-calculator__results-subheading">
            {labels.fieldMembraneHeading}
          </h3>
          {/* This is filtered by resin types */}
          <div className="material-estimating-calculator__results-table-wrapper">
            <div className="material-estimating-calculator__results-grid">
              {(config.resinType === 'terapro220' ||
                config.resinType === 'terapro250') &&
                renderMobileSections(
                  labels.primerLayerHeading,
                  config.membranePrimerLayerDropdownValue,
                  config.setMembranePrimerLayerDropdownValue,
                  [
                    { value: 'proPrimerT', label: productNames.proPrimerT },
                    {
                      value: 'proPrimerCatalystLiquidT',
                      label: productNames.proPrimerCatalystLiquidT
                    }
                  ]
                )}
              {config.resinType === 'terapro250grid' &&
                renderMobileSections(
                  labels.primerLayerHeading,
                  config.membranePrimerLayerDropdownValue250Grid,
                  config.setMembranePrimerLayerDropdownValue250Grid,
                  [
                    {
                      value: 'proPrimerWGrid',
                      label: productNames.proPrimerWGrid
                    },
                    {
                      value: 'proGridReinforcement',
                      label: productNames.proGridReinforcement
                    },
                    {
                      value: 'proPrimerWGridCatalystLiquid',
                      label: productNames.proPrimerWGridCatalystLiquid
                    }
                  ]
                )}
              {config.resinType === 'terapro220' &&
                renderMobileSections(
                  labels.surfacingLayerHeading,
                  config.surfacingLayerDropdownValue220,
                  config.setSurfacingLayerDropdownValue220,
                  [
                    {
                      value: 'terapro220ResinAggregate',
                      label: productNames.terapro220ResinAggregate
                    },
                    {
                      value: 'terapro220ResinAggregateCatalystPowder',
                      label: productNames.terapro220ResinAggregateCatalystPowder
                    }
                  ]
                )}
              {config.resinType === 'terapro250' &&
                renderMobileSections(
                  labels.surfacingLayerHeading,
                  config.surfacingLayerDropdownValue250,
                  config.setSurfacingLayerDropdownValue250,
                  [
                    {
                      value: 'terapro250ResinAggregate',
                      label: productNames.terapro250ResinAggregate
                    },
                    {
                      value: 'terapro250ResinAggregateCatalystPowder',
                      label: productNames.terapro250ResinAggregateCatalystPowder
                    }
                  ]
                )}
              {config.resinType === 'terapro250grid' &&
                renderMobileSections(
                  labels.surfacingLayerHeading,
                  config.surfacingLayerDropdownValue250Grid,
                  config.setSurfacingLayerDropdownValue250Grid,
                  [
                    {
                      value: 'terapro250GridResinAggregate',
                      label: productNames.terapro250GridResinAggregate
                    },
                    {
                      value: 'terapro250GridResinAggregateCatalystPowder',
                      label:
                        productNames.terapro250GridResinAggregateCatalystPowder
                    }
                  ]
                )}
            </div>
            <table className="material-estimating-calculator__results-table">
              <thead>
                <tr>
                  <th className="material-estimating-calculator__results-table-title">
                    {labels.primerLayerHeading}
                  </th>
                </tr>
                <tr>
                  <th>{labels.materialHeading}</th>
                  <th>{labels.typicalCoverageHeading}</th>
                  <th>{labels.unitSizeHeading}</th>
                  <th>{labels.unitQuantityHeading}</th>
                </tr>
              </thead>
              <tbody>
                {(config.resinType === 'terapro220' ||
                  config.resinType === 'terapro250') && (
                  <>
                    {renderDesktopResultsRow('proPrimerT')}
                    {renderDesktopResultsRow('proPrimerCatalystLiquidT')}
                  </>
                )}
                {config.resinType === 'terapro250grid' && (
                  <>
                    {renderDesktopResultsRow('proPrimerWGrid')}
                    {renderDesktopResultsRow('proGridReinforcement')}
                    {renderDesktopResultsRow('proPrimerWGridCatalystLiquid')}
                  </>
                )}
                <tr>
                  <th className="material-estimating-calculator__results-table-title">
                    {labels.surfacingLayerHeading}
                  </th>
                </tr>
                {config.resinType === 'terapro220' && (
                  <>
                    {renderDesktopResultsRow('terapro220ResinAggregate')}
                    {renderDesktopResultsRow(
                      'terapro220ResinAggregateCatalystPowder'
                    )}
                  </>
                )}
                {config.resinType === 'terapro250' && (
                  <>
                    {renderDesktopResultsRow('terapro250ResinAggregate')}
                    {renderDesktopResultsRow(
                      'terapro250ResinAggregateCatalystPowder'
                    )}
                  </>
                )}
                {config.resinType === 'terapro250grid' && (
                  <>
                    {renderDesktopResultsRow('terapro250GridResinAggregate')}
                    {renderDesktopResultsRow(
                      'terapro250GridResinAggregateCatalystPowder'
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );

  const renderMobileTotalSections = () => {
    const { unitQuantityRoundedUpInteger, unitLabel, quotedCost, totalCost } =
      config.results[config.totalDropdownValue];
    const { totalDropdownValue, setTotalDropdownValue } = config;
    return (
      <>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label material-estimating-calculator__results-label--blue">
            {labels.materialHeading}
          </p>
          <select
            className="material-estimating-calculator__results-select"
            onChange={e => setTotalDropdownValue(e.target.value)}
            value={totalDropdownValue}
          >
            <option value="proPrimerW">{productNames.proPrimerW}</option>
            {config.resinType !== 'terapro250grid' && (
              <option value="proPrimerT">{productNames.proPrimerT}</option>
            )}
            <option value="pmmaFlashing">{productNames.pmmaFlashing}</option>
            <option value="proFleeceReinforcement">
              {productNames.proFleeceReinforcement}
            </option>
            {config.includeProColorFinish && (
              <option value="proColorFinish">
                {productNames.proColorFinish}
              </option>
            )}
            <option value="proCatalystLiquidTotal">
              {productNames.proCatalystLiquidTotal}
            </option>
            {config.resinType === 'terapro220' && (
              <>
                <option value="terapro220ResinAggregate">
                  {productNames.terapro220ResinAggregate}
                </option>
                <option value="terapro220ResinAggregateCatalystPowder">
                  {productNames.terapro220ResinAggregateCatalystPowder}
                </option>
              </>
            )}
            {config.resinType === 'terapro250' && (
              <>
                <option value="terapro250ResinAggregate">
                  {productNames.terapro250ResinAggregate}
                </option>
                <option value="terapro250ResinAggregateCatalystPowder">
                  {productNames.terapro250ResinAggregateCatalystPowder}
                </option>
              </>
            )}
            {config.resinType === 'terapro250grid' && (
              <>
                <option value="terapro250GridResinAggregate">
                  {productNames.terapro250GridResinAggregate}
                </option>
                <option value="proGridReinforcement">
                  {productNames.proGridReinforcement}
                </option>
                <option value="terapro250GridResinAggregateCatalystPowder">
                  {productNames.terapro250GridResinAggregateCatalystPowder}
                </option>
              </>
            )}
          </select>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.totalNumberOfUnitsHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {formatNumber(unitQuantityRoundedUpInteger, {
              suffix: ` ${unitLabel}`,
              noZero: true
            })}
          </p>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.quotedCostPerUnitHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {formatNumber(quotedCost, {
              prefix: '$',
              suffix: `/${unitLabel}`
            })}
          </p>
        </div>
        <div className="material-estimating-calculator__results-row">
          <p className="material-estimating-calculator__results-label">
            {labels.totalCostPerMaterialHeading}
          </p>
          <p className="material-estimating-calculator__results-text">
            {formatNumber(totalCost, { prefix: '$' })}
          </p>
        </div>
      </>
    );
  };

  const totalProductNeededRowRender = (product, hasRtf) => {
    const { unitQuantityRoundedUpInteger, unitLabel, quotedCost, totalCost } =
      config.results[product];
    const tooltip = tooltips[product];

    return (
      <tr>
        <td>
          <span className="material-estimating-calculator__column-title">
            <span
              className={
                tooltip
                  ? 'material-estimating-calculator__tooltip-wrapper'
                  : null
              }
            >
              {hasRtf ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: JSON.parse(props.productsDescriptionsRtf)[product]
                  }}
                ></span>
              ) : (
                productNames[product]
              )}
              {tooltip && (
                <button
                  className="material-estimating-calculator__tooltip"
                  aria-label="tooltip"
                  onMouseOver={handleTooltipShow}
                  onFocus={handleTooltipShow}
                  onTouchStart={handleTooltipShow}
                >
                  <div
                    className="material-estimating-calculator__tooltip-box"
                    dangerouslySetInnerHTML={{ __html: tooltip }}
                  ></div>
                </button>
              )}
            </span>
          </span>
        </td>
        <td>
          {formatNumber(unitQuantityRoundedUpInteger, {
            suffix:
              unitLabel === 'box'
                ? ` ${unitLabel}${unitQuantityRoundedUpInteger > 1 ? 'es' : ''}`
                : ` ${unitLabel}${
                    unitQuantityRoundedUpInteger > 1 ? 's' : ''
                  } `,
            noZero: true
          })}
        </td>
        {config.includePrice && (
          <>
            <td>
              {formatNumber(quotedCost, {
                prefix: '$',
                suffix: `/${unitLabel}`
              })}
            </td>
            <td>
              {formatNumber(totalCost, {
                prefix: '$'
              })}
            </td>
          </>
        )}
      </tr>
    );
  };

  const totalProductNeededRender = () => {
    //config.resinTypes: '', terapro220, terapro250, terapro250grid // this is needed to filter the results
    if (!config.displayResults) return null;

    return (
      <>
        <div className="material-estimating-calculator__results material-estimating-calculator__results--total">
          <h2 className="material-estimating-calculator__results-heading">
            <span
              className={
                (!config.includePrice && labels.totalProductNeededTooltip) ||
                (config.includePrice &&
                  labels.estimatedMaterialCostBreakdownTooltip)
                  ? 'material-estimating-calculator__results-heading-text'
                  : null
              }
            >
              <span className="material-estimating-calculator__results-heading-inner">
                {config.includePrice
                  ? labels.estimatedMaterialCostBreakdownHeading
                  : labels.totalProductNeededHeading}

                {((!config.includePrice && labels.totalProductNeededTooltip) ||
                  (config.includePrice &&
                    labels.estimatedMaterialCostBreakdownTooltip)) && (
                  <button
                    className="material-estimating-calculator__tooltip"
                    aria-label="tooltip"
                    onMouseOver={handleTooltipShow}
                    onFocus={handleTooltipShow}
                    onTouchStart={handleTooltipShow}
                  >
                    <div
                      className="material-estimating-calculator__tooltip-box"
                      dangerouslySetInnerHTML={{
                        __html: config.includePrice
                          ? labels.estimatedMaterialCostBreakdownTooltip
                          : labels.totalProductNeededTooltip
                      }}
                    ></div>
                  </button>
                )}
              </span>
            </span>
          </h2>
          {/* This is for all the resin types */}
          <div className="material-estimating-calculator__results-table-wrapper material-estimating-calculator__results-table-wrapper--total">
            {config.includePrice && (
              <div className="material-estimating-calculator__results-grid">
                {renderMobileTotalSections()}
              </div>
            )}
            <table
              className={`material-estimating-calculator__results-table ${
                !config.includePrice
                  ? 'material-estimating-calculator__results-table--mobile'
                  : ''
              }`}
            >
              <thead>
                <tr>
                  <th>{labels.materialHeading}</th>
                  <th>{labels.totalNumberOfUnitsHeading}</th>
                  {config.includePrice && (
                    <>
                      <th>{labels.quotedCostPerUnitHeading}</th>
                      <th>{labels.totalCostPerMaterialHeading}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {/* proPrimerW */}
                {totalProductNeededRowRender('proPrimerW', true)}
                {/* proPrimerT */}
                {config.resinType !== 'terapro250grid' &&
                  totalProductNeededRowRender('proPrimerT', true)}
                {/* pmmaFlashing */}
                {totalProductNeededRowRender('pmmaFlashing')}
                {/* proFleeceReinforcement */}
                {totalProductNeededRowRender('proFleeceReinforcement')}
                {/* proColorFinish*/}
                {config.includeProColorFinish &&
                  totalProductNeededRowRender('proColorFinish')}
                {/* proCatalystLiquidTotal */}
                {totalProductNeededRowRender('proCatalystLiquidTotal')}
                {config.resinType === 'terapro220' && (
                  <>
                    {/* terapro220ResinAggregate */}
                    {totalProductNeededRowRender('terapro220ResinAggregate')}
                    {/* terapro220ResinAggregateCatalystPowder */}
                    {totalProductNeededRowRender(
                      'terapro220ResinAggregateCatalystPowder'
                    )}
                  </>
                )}
                {config.resinType === 'terapro250' && (
                  <>
                    {/* terapro250ResinAggregate */}
                    {totalProductNeededRowRender('terapro250ResinAggregate')}
                    {/* terapro250ResinAggregateCatalystPowder */}
                    {totalProductNeededRowRender(
                      'terapro250ResinAggregateCatalystPowder'
                    )}
                  </>
                )}
                {config.resinType === 'terapro250grid' && (
                  <>
                    {/* terapro250GridResinAggregate */}
                    {totalProductNeededRowRender(
                      'terapro250GridResinAggregate'
                    )}
                    {/* proGridReinforcement */}
                    {totalProductNeededRowRender('proGridReinforcement')}
                    {/* terapro250GridResinAggregateCatalystPowder */}
                    {totalProductNeededRowRender(
                      'terapro250GridResinAggregateCatalystPowder'
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {config.includePrice && (
          <p className="material-estimating-calculator__total">
            <span className="material-estimating-calculator__total-label">
              {labels.totalEstimatedMaterialCost}
            </span>{' '}
            <span className="material-estimating-calculator__value">
              ${config.totalEstimatedMaterialCost.toLocaleString()}
            </span>
          </p>
        )}
      </>
    );
  };

  return (
    <div ref={printContainerRef}>
      {/* Input form */}
      <form className="material-estimating-calculator__form" autoComplete="off">
        <div className="material-estimating-calculator__form-row">
          <SelectTopLabel
            className="material-estimating-calculator__resin-type"
            label={labels.resinType}
            options={config.optionsResinTypes}
            fieldProps={{ onChange: config.handleChangeResinType }}
            placeholder={labels.resinTypePlaceholder}
            filled={config.resinType}
          />
        </div>
        {/* Temperature, Area, Flashing, Waste */}
        <div className="material-estimating-calculator__form-row">
          <Input
            className="material-estimating-calculator__form-input material-estimating-calculator__form-input--temperature form-input__field--filled"
            id="calculator-temperature"
            name={labels.temperature}
            aria-label={labels.temperature}
            label={labels.temperature}
            placeholder={labels.temperaturePlaceholder}
            handleChange={config.handleChangeTemperature}
            handleBlur={config.handleBlurTemperature}
            value={config.formattedTemperature}
            {...(config.temperatureError && {
              errorMessage: labels.temperatureError
            })}
            inputMode="numeric"
          />
          <Input
            className="material-estimating-calculator__form-input material-estimating-calculator__form-input--area form-input__field--filled"
            id="calculator-area"
            name={labels.area}
            aria-label={labels.area}
            label={labels.area}
            placeholder={labels.areaPlaceholder}
            handleChange={config.handleChangeArea}
            handleBlur={config.handleBlurArea}
            value={config.formattedArea}
            {...(config.areaError && {
              errorMessage: labels.areaError
            })}
            inputMode="numeric"
          />
          <Input
            className="material-estimating-calculator__form-input material-estimating-calculator__form-input--flashing form-input__field--filled"
            id="calculator-flashing"
            name={labels.flashing}
            aria-label={labels.flashing}
            label={labels.flashing}
            placeholder={labels.flashingPlaceholder}
            handleChange={config.handleChangeFlashing}
            handleBlur={config.handleBlurFlashing}
            value={config.formattedFlashing}
            {...(config.flashingError && {
              errorMessage: labels.flashingError
            })}
            inputMode="numeric"
          />
          <Input
            className="material-estimating-calculator__form-input material-estimating-calculator__form-input--waste form-input__field--filled"
            id="calculator-waste"
            name={labels.waste}
            aria-label={labels.waste}
            label={labels.waste}
            handleChange={config.handleChangeWaste}
            handleBlur={config.handleBlurWaste}
            value={config.formattedWaste}
            {...(config.wasteError && {
              errorMessage: labels.wasteError
            })}
            inputMode="numeric"
          />
        </div>
        {/* Quoted Prices  */}
        <div className="material-estimating-calculator__wrapper">
          <div className="material-estimating-calculator__checkbox-wrapper">
            <p>Include price in calculation estimate</p>
            <div className="material-estimating-calculator__checkbox-group">
              <Checkbox
                id="calculator-price-yes"
                name={labels.yes}
                label={labels.yes}
                value="yes"
                checked={config.includePrice}
                handleChange={config.handleChangePriceYes}
              />
              <Checkbox
                id="calculator-price-no"
                name={labels.no}
                label={labels.no}
                value="no"
                checked={config.includePrice !== '' && !config.includePrice}
                handleChange={config.handleChangePriceNo}
              />
            </div>
          </div>
          {config.includePrice && (
            <div className="material-estimating-calculator__form-row">
              <Input
                className="material-estimating-calculator__form-input material-estimating-calculator__form-input--quoted-pro-primer form-input__field--filled"
                id="calculator-quoted-pro-primer"
                name={labels.quotedPriceProPrimer}
                aria-label={labels.quotedPriceProPrimer}
                label={labels.quotedPriceProPrimer}
                placeholder={labels.quotedPricePlaceholder}
                handleChange={config.handleChangeQuotedPriceProPrimer}
                handleBlur={config.handleBlurQuotedPriceProPrimer}
                value={config.formattedQuotedPriceProPrimer}
                inputMode="numeric"
              />
              <Input
                className="material-estimating-calculator__form-input material-estimating-calculator__form-input--quoted-resin form-input__field--filled"
                id="calculator-quoted-resin"
                name={labels.quotedPriceResin}
                aria-label={labels.quotedPriceResin}
                label={labels.quotedPriceResin}
                placeholder={labels.quotedPricePlaceholder}
                handleChange={config.handleChangeQuotedPriceResin}
                handleBlur={config.handleBlurQuotedPriceResin}
                value={config.formattedQuotedPriceResin}
                inputMode="numeric"
              />
              <Input
                className="material-estimating-calculator__form-input material-estimating-calculator__form-input--quoted-catalyst-liquid form-input__field--filled"
                id="calculator-quoted-catalyst-liquid"
                name={labels.quotedPriceCatalystLiquid}
                aria-label={labels.quotedPriceCatalystLiquid}
                label={labels.quotedPriceCatalystLiquid}
                placeholder={labels.quotedPricePlaceholder}
                handleChange={config.handleChangeQuotedPriceCatalystLiquid}
                handleBlur={config.handleBlurQuotedPriceCatalystLiquid}
                value={config.formattedQuotedPriceCatalystLiquid}
                inputMode="numeric"
              />
              <Input
                className="material-estimating-calculator__form-input material-estimating-calculator__form-input--quoted-catalyst-powder form-input__field--filled"
                id="calculator-quoted-catalyst-powder"
                name={labels.quotedPriceCatalystPowder}
                aria-label={labels.quotedPriceCatalystPowder}
                label={labels.quotedPriceCatalystPowder}
                placeholder={labels.quotedPricePlaceholder}
                handleChange={config.handleChangeQuotedPriceCatalystPowder}
                handleBlur={config.handleBlurQuotedPriceCatalystPowder}
                value={config.formattedQuotedPriceCatalystPowder}
                inputMode="numeric"
              />
            </div>
          )}
        </div>
        {/* Pro Color Finish */}
        <div className="material-estimating-calculator__checkbox-wrapper">
          <p>Include Pro Color Finish in estimate</p>
          <div className="material-estimating-calculator__checkbox-group">
            <Checkbox
              id="calculator-color-pro-finish-yes"
              name={labels.yes}
              label={labels.yes}
              value="yes"
              checked={config.includeProColorFinish}
              handleChange={config.handleChangeProColorFinishYes}
            />
            <Checkbox
              id="calculator-color-pro-finish-no"
              name={labels.no}
              label={labels.no}
              value="no"
              checked={
                config.includeProColorFinish !== '' &&
                !config.includeProColorFinish
              }
              handleChange={config.handleChangeProColorFinishNo}
            />
          </div>
          {config.includeProColorFinish && config.includePrice && (
            <div className="material-estimating-calculator__form-row">
              <Input
                className="material-estimating-calculator__form-input material-estimating-calculator__form-input--quoted-price-pro-color-finish form-input__field--filled"
                id="calculator-quoted-pro-primer"
                name={labels.quotedPriceProColorFinish}
                aria-label={labels.quotedPriceProColorFinish}
                label={labels.quotedPriceProColorFinish}
                placeholder={labels.quotedPriceProColorFinish}
                handleChange={config.handleChangeQuotedPriceProColorFinish}
                handleBlur={config.handleBlurQuotedPriceProColorFinish}
                value={config.formattedQuotedPriceProColorFinish}
                inputMode="numeric"
              />
            </div>
          )}
        </div>
        {/* Disclaimer */}
        {calculatorDisclaimerDiv}
        {/* Calculate button */}
        <Button
          className="material-estimating-calculator__form-input material-estimating-calculator__form-input--calculate"
          label={labels.calculate}
          large={true}
          outline={false}
          darkmode={false}
          disabled={config.disableButton}
          onClick={config.handleClickCalculate}
        />
      </form>
      {/* Results */}
      {resultsRender}
      {/* Totals */}
      {totalProductNeededRender()}
      {/* Save as PDF button */}
      {config.displayResults && (
        <>
          <Button
            ref={savePdfButtonRef}
            className="material-estimating-calculator__form-input material-estimating-calculator__form-input--save-as-pdf"
            label={labels.saveAsPdf}
            large={true}
            outline={false}
            darkmode={false}
            disabled={config.disableButton}
            onClick={handleClickSaveAsPdf}
            dataProps={{
              'data-trigger-modal': labels.calculatorModalId,
              'data-title': labels.calculatorModalTitle
            }}
          />
          {saveDisclaimerDiv}
        </>
      )}
    </div>
  );
}

export default MaterialEstimatingCalculator;
